/**
 * Foto del cliente en las vistas 360 (expediente del préstamo y del cliente).
 *
 * El avatar de la cabecera era decorativo: 64px y sin nada detrás. Aquí se
 * pulsa para verla en grande y, con el flag `modificar` del expediente, se
 * reemplaza sin salir a Cobro de pagos (que era el único lugar donde se
 * podía cambiar). La imagen vive en expedienteunico.foto, una sola por
 * documento: la misma que ven las dos vistas y la que imprime el incauto.
 *
 *   <FotoCliente cedula={r.cedula} nombre={r.nombres} fotoB64={d.foto_b64}
 *     puedeEditar={puedeAccionPrograma(roleDetalles, "expediente", "modificar")}
 *     noprest={r.noprest} onCambiada={(b64) => ...} />
 */
import { useEffect, useRef, useState } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import SaveIcon from "@mui/icons-material/Save";
import { useGlobalMDAlert } from "context";
import { FP } from "components/financepro";
import { useApi } from "lib/api";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

// El servidor reduce la foto a 1600 px antes de guardarla (app/imagenes.py),
// así que aquí solo se corta lo absurdo: una foto de celular de 4-8MB sube
// entera y vuelve nítida, que es lo que antes no pasaba.
const MAX_BYTES = 12 * 1024 * 1024;

export default function FotoCliente({
  cedula,
  nombre = "",
  fotoB64 = "",
  puedeEditar = false,
  // Solo para la bitácora: deja el evento colgado también del préstamo
  // desde el que se cambió la foto.
  noprest = "",
  onCambiada,
  size = 64,
}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  // La foto vigente se mantiene aquí para que el avatar cambie al guardar
  // sin recargar el expediente completo; el prop manda cuando el padre
  // vuelve a cargar (otro préstamo, otra cédula).
  const [foto, setFoto] = useState(fotoB64 || "");
  const [abierto, setAbierto] = useState(false);
  const [nueva, setNueva] = useState("");
  const [guardando, setGuardando] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setFoto(fotoB64 || "");
  }, [fotoB64]);

  const cerrar = () => {
    if (guardando) return;
    setAbierto(false);
    setNueva("");
  };

  const seleccionar = (e) => {
    const archivo = e.target.files?.[0];
    e.target.value = ""; // permite volver a elegir el mismo archivo
    if (!archivo) return;
    if (!archivo.type.startsWith("image/")) {
      showAlert({ message: "El archivo debe ser una imagen", color: "warning" });
      return;
    }
    if (archivo.size > MAX_BYTES) {
      showAlert({ message: "La imagen no puede exceder 12MB", color: "warning" });
      return;
    }
    const lector = new FileReader();
    lector.onload = () => {
      const b64 = String(lector.result || "").split(",")[1] || "";
      if (b64) setNueva(b64);
      else showAlert({ message: "No se pudo leer la imagen", color: "error" });
    };
    lector.onerror = () => showAlert({ message: "No se pudo leer la imagen", color: "error" });
    lector.readAsDataURL(archivo);
  };

  const guardar = async () => {
    if (!nueva) return;
    if (!cedula) {
      showAlert({ message: "El cliente no tiene documento registrado", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/expediente_guardarfoto", {
        cedula: String(cedula),
        foto_b64: nueva,
        noprest: noprest ? String(noprest) : "",
      });
      if (ok) {
        setFoto(nueva);
        setNueva("");
        setAbierto(false);
        if (onCambiada) onCambiada(nueva);
        showAlert({ message: data.ok || "Foto actualizada", color: "success" });
      } else {
        showAlert({ message: data.Error || "No se pudo guardar la foto", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  const mostrada = nueva || foto;
  let titulo = "Este cliente no tiene foto";
  if (puedeEditar) titulo = "Ver la foto en grande o reemplazarla";
  else if (mostrada) titulo = "Ver la foto en grande";

  return (
    <>
      <Tooltip title={titulo}>
        <Avatar
          src={foto ? `data:image/jpeg;base64,${foto}` : undefined}
          alt={nombre || "Foto del cliente"}
          onClick={() => setAbierto(true)}
          sx={{
            width: size,
            height: size,
            bgcolor: FP.color.surfaceAlt,
            color: FP.color.textSecondary,
            cursor: "pointer",
            transition: "box-shadow .15s, transform .15s",
            "&:hover": { boxShadow: FP.shadow.soft, transform: "scale(1.04)" },
          }}
        >
          <PersonOutlineIcon />
        </Avatar>
      </Tooltip>

      <Dialog
        open={abierto}
        onClose={cerrar}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Foto del cliente
          {nombre ? (
            <Typography
              sx={{ fontSize: "0.82rem", color: FP.color.textSecondary, fontWeight: 500 }}
            >
              {nombre}
            </Typography>
          ) : null}
        </DialogTitle>
        <DialogContent>
          <Stack alignItems="center" spacing={1.5} sx={{ py: 1 }}>
            {mostrada ? (
              <Box
                component="img"
                alt={nombre || "Foto del cliente"}
                src={`data:image/jpeg;base64,${mostrada}`}
                sx={{
                  // Visor mediano: ocupa el ancho del diálogo y hasta 70vh
                  // de alto. `contain` deja las bandas del fondo a los lados
                  // en las fotos verticales en vez de deformarlas.
                  width: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                  borderRadius: 2,
                  border: `1px solid ${FP.color.border}`,
                  bgcolor: FP.color.surfaceAlt,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  py: 6,
                  borderRadius: 2,
                  border: `1px dashed ${FP.color.borderStrong}`,
                  bgcolor: FP.color.surfaceAlt,
                  textAlign: "center",
                  color: FP.color.textSecondary,
                }}
              >
                <PersonOutlineIcon sx={{ fontSize: 46, opacity: 0.6 }} />
                <Typography sx={{ fontSize: "0.9rem" }}>
                  Este cliente no tiene foto registrada
                </Typography>
              </Box>
            )}

            {puedeEditar ? (
              <>
                <Button
                  startIcon={<PhotoCameraOutlinedIcon />}
                  onClick={() => inputRef.current?.click()}
                  disabled={guardando}
                  sx={btnOutline(FP.color.navy)}
                >
                  {mostrada ? "Cambiar imagen" : "Seleccionar imagen"}
                </Button>
                <input ref={inputRef} hidden type="file" accept="image/*" onChange={seleccionar} />
                {nueva ? (
                  <Typography sx={{ fontSize: "0.8rem", color: FP.color.textSecondary }}>
                    Vista previa de la imagen nueva — guarde para aplicarla
                  </Typography>
                ) : null}
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={cerrar} disabled={guardando} sx={btnOutline(FP.color.textSecondary)}>
            {nueva ? "Cancelar" : "Cerrar"}
          </Button>
          {puedeEditar ? (
            <Button
              disableElevation
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={guardar}
              disabled={guardando || !nueva}
              sx={btnSolid(FP.color.accentBlue, FP.color.navy)}
            >
              Guardar foto
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  );
}
